import { AnimationEvent } from '@angular/animations';
import { BreakpointObserver } from '@angular/cdk/layout';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { Breakpoints, listItem } from '@lockton-apps/shared/ui/lui';
import { Dictionary } from '@ngrx/entity';
import { filter, includes, isFunction, map, reject, some, take } from 'lodash';
import { Observable } from 'rxjs';
import { map as rxMap } from 'rxjs/operators';
import { FormlyFieldConfig } from '@ngx-formly/core';

import { FieldMetaPipe } from '../pipes/field-meta/field-meta.pipe';
import { DynamicFormCustomEvent } from '../dynamic-field-type/dynamic-field-type.component';

export interface DynamicFormTableRow {
  item: any;
  __meta: DynamicFormTableRowMeta;
}

export interface DynamicFormTableRowMeta {
  id: number;
  valid: boolean;
}

@Component({
    selector: 'lockton-apps-dynamic-form-table',
    templateUrl: './dynamic-form-table.component.html',
    styleUrls: ['./dynamic-form-table.component.scss'],
    animations: [listItem],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DynamicFormTableComponent implements OnChanges, OnInit {
  @Input() label!: string;
  @Input() limit!: number;
  @Input() rows!: DynamicFormTableRow[];
  @Input() fields!: FormlyFieldConfig[];
  @Input() readonly!: boolean | undefined;
  @Input() print!: boolean | undefined;
  @Input() set totalCountFilter(script: string) {
    if (script) {
      const parsedScript = eval(script);

      if (isFunction(parsedScript)) {
        this._totalCountFilter = parsedScript;
      }
    }
  }

  @Output() edit: EventEmitter<number> = new EventEmitter();
  @Output() delete: EventEmitter<number> = new EventEmitter();
  @Output() customEvent = new EventEmitter<DynamicFormCustomEvent>();
  @Output() animationDone: EventEmitter<AnimationEvent> = new EventEmitter();

  data!: any[];
  columnIds!: string[];
  columnsMeta: Dictionary<any> = {};
  showAllRows!: boolean;
  hasInvalidRows!: boolean;
  isWideView$: Observable<boolean> = this.breakpointObserver
    .observe(Breakpoints.GT_SM)
    .pipe(rxMap(({ matches }) => matches));

  get rowCount() {
    return filter(this.rows, (row) => this._totalCountFilter(row.item)).length;
  }

  constructor(
    private readonly meta: FieldMetaPipe,
    private readonly breakpointObserver: BreakpointObserver
  ) {}

  ngOnInit(): void {
    this.buildColumns(this.fields, this.readonly, this.print);
  }

  ngOnChanges(): void {
    this.buildData(this.rows, this._limit);
  }

  trackByFn(index: number, item: DynamicFormTableRow): string | number {
    return item.__meta.id;
  }

  toggleRowsVisibility(): void {
    this.showAllRows = !this.showAllRows;
    this.buildData(this.rows, this._limit);
  }

  private get _limit(): number {
    return this.showAllRows ? Infinity : this.limit;
  }

  private buildData(rows: DynamicFormTableRow[], limit: number): void {
    this.data = limit === Infinity ? rows : take(rows, limit);
    this.hasInvalidRows = some(rows, { __meta: { valid: false } });
  }

  private buildColumns(
    fields: FormlyFieldConfig[],
    readonly?: boolean,
    print?: boolean
  ): void {
    const filteredFields: FormlyFieldConfig[] = reject(
      fields,
      ({ type }) => !type || includes(['hidden', 'template'], type)
    );

    this.columnIds = map(filteredFields, (field) => {
      this.columnsMeta[field.key as string] = this.meta.transform(
        { key: field.key, value: undefined },
        fields
      );

      return field.key as string;
    });

    if (!readonly && !print) {
      this.columnIds.push('actions');
    }
  }

  private _totalCountFilter(item: any) {
    return !!item;
  }
}
